import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ThemeProvider,
} from '@mui/material';
import {AppStoreProvider} from '../../stores/appStore';
import NavBar from '../../pieces/navbar/navbar';
import myTheme from '../../styles/myStyles';
import {AiCurrentCalls} from './panels/aiCurrentCalls';
import {AiAgents} from './panels/aiAgents';
import {AiDoNotCalls} from './panels/aiDoNotCalls';
import {AiAttentionCalls} from './panels/aiAttentionCalls';
import {AiActiveCalls} from './panels/aiActiveCalls';
import {AiSummaryCards} from './panels/aiSummaryCards';
import {urlSearchParams} from '../../baseUrls';
import AllInspectionsView from '../jobs/ipermitJobPages/listViews/inspections/allInspectionsView';
import {inspectionApiTypes} from '../jobs/ipermitJobPages/listViews/inspections/inspectionsGridOptions';
import {drawerWidth} from '../wrappers/recordBodyWrapper';
import * as STYLE_CONSTANTS from '../../styles/styleConstants';
import {CustomMediaQuery} from '../../hooks/customMediaQuery';

const {COLORS, HEIGHT} = STYLE_CONSTANTS;

/** The fit below never squeezes a grid shorter than this; past it, the page scrolls instead. */
const MIN_GRID_HEIGHT = 200;

/**
 * The inspections grid as a dashboard panel. Both inspection tabs render this same element tree,
 * so `key` is what makes switching between them remount the grid; without it React reuses the
 * instance and carries the other tab's selected view, filters and paging across.
 */
const inspectionsPanel = (key, viewProps = {}) => (
  <>
    <NavBar />
    <ThemeProvider theme={myTheme}>
      <AllInspectionsView key={key} suppressNavTopPadding fromAiDashboard {...viewProps} />
    </ThemeProvider>
  </>
);

/**
 * Tab definitions for the AI dashboard (same shape as companyView `getTabs()`:
 * `name`, `api_name`, `panel`, `order`).
 *
 * `viewAgentAiCall` splits the page's two audiences. An agent works their own calls, so they get
 * their calls, the inspections they are working, and the Do Not Call list they may need to check
 * mid-call. Everyone else's calls and the agent roster are a supervisor's view; the matching
 * endpoints enforce the same split, so hiding the tabs is presentation, not the control.
 */
const getTabs = (canViewAllAgentCalls) => {
  const tabs = {
    allInspections: {
      name: 'All Inspections',
      api_name: 'allInspections',
      panel: inspectionsPanel('allInspections'),
      order: 0,
    },
    /**
     * Inspections whose AI call ended in an outcome a person has to review (AI status Verify).
     * This is the agents' main work queue, so it sits next to All Inspections rather than among
     * the call-level tabs.
     */
    needsAttention: {
      name: 'Needs Verify',
      api_name: 'needsAttention',
      panel: inspectionsPanel('needsAttention', {
        lockedView: {
          value: inspectionApiTypes.aiNeedToVerify,
          // The title it had in the All Inspections menu, which keeps saved custom views attached.
          title: 'Need to Verify AI Calls',
          heading: 'Needs Verify',
        },
      }),
      order: 0.5,
    },
    myCalls: {
      name: 'My AI calls',
      api_name: 'myCalls',
      panel: <AiCurrentCalls showAll={false} />,
      order: 1,
    },
  };
  if (canViewAllAgentCalls) {
    tabs.allCalls = {
      name: 'All AI calls',
      api_name: 'allCalls',
      panel: <AiCurrentCalls showAll={true} />,
      order: 2,
    };
    /**
     * Calls still open right now, including the ones that never reported finishing. Supervisor
     * only: it is the view an open call is closed from, and an open call holds a slot in the
     * concurrency count every agent's dispatch depends on.
     */
    tabs.activeCalls = {
      name: 'Active Calls',
      api_name: 'activeCalls',
      panel: <AiActiveCalls />,
      order: 2.5,
    };
    tabs.agents = {
      name: 'Agents',
      api_name: 'agents',
      panel: <AiAgents />,
      order: 3,
    };
  }
  /**
   * Visible to agents as well as supervisors: an agent who just took an inbound call the AI could
   * not place is exactly who can identify the property, and linking is agent work.
   */
  tabs.callExceptions = {
    name: 'Call Exceptions',
    api_name: 'callExceptions',
    panel: <AiAttentionCalls />,
    order: 4,
  };
  tabs.doNotCall = {
    name: 'Do Not Call',
    api_name: 'doNotCall',
    panel: <AiDoNotCalls />,
    order: 5,
  };
  return tabs;
};

const AiDashboardInner = () => {
  const user = useSelector((state) => state.user);

  const canViewAllAgentCalls = !!user?.viewAgentAiCall;
  const tabs = useMemo(() => getTabs(canViewAllAgentCalls), [canViewAllAgentCalls]);
  const sortedTabs = Object.values(tabs).sort((a, b) => a.order - b.order);
  const [openTab, setOpenTab] = useState(0);
  const query = CustomMediaQuery();
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Card clicks land on a tab, and for the inspections grid also select a view.
   *
   * The view is handed over through the `view` search param rather than a prop: `useViewMenu`
   * already reads that param, both on mount and whenever the query string changes, so this works
   * whether the inspections tab is already open or about to be.
   */
  const handleCardNavigate = useCallback((target) => {
    if (!target) return;

    const tabIndexFor = (apiName) => sortedTabs.findIndex(
      (tab) => tab.api_name === apiName,
    );

    let index = tabIndexFor(target.tab);
    if (index < 0 && target.fallbackTab) {
      index = tabIndexFor(target.fallbackTab);
    }
    if (index < 0) return;

    if (target.view) {
      const params = new URLSearchParams(searchParams);
      params.set(urlSearchParams.view, target.view);
      setSearchParams(params);
    }

    setOpenTab(index);
  }, [sortedTabs, searchParams, setSearchParams]);

  useEffect(() => {
    const maxIndex = Math.max(0, sortedTabs.length - 1);
    setOpenTab((prev) => Math.min(prev, maxIndex));
  }, [sortedTabs.length]);

  /**
   * Keeps the whole page inside the window. The grids size themselves to the window
   * (`AlpineContainer`'s `100vh` formula), not to the space left here, so the cards above them and
   * this page's own header and paging rows would push their bottom edge - horizontal scrollbar and
   * page controls included - off screen.
   *
   * The difference goes to the grids as `--grid-extra-offset`: start from the cards' height, let
   * the layout settle, then take off whatever still overflows, the grid being the only part that
   * can give. Measured rather than fixed: the cards wrap on narrower windows and each tab's
   * header differs. Set on the element directly, not through state, so a pass is one layout and
   * no re-render.
   */
  const mainRef = useRef(null);
  const cardsRef = useRef(null);
  const panelRef = useRef(null);
  useEffect(() => {
    const main = mainRef.current;
    const panel = panelRef.current;
    if (!main || !panel) return undefined;
    // A timer, not requestAnimationFrame: frames never run in a tab that is not on screen, so a
    // dashboard opened in the background would stay unfitted until something resized it.
    let timer = 0;
    const fitGrids = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const base = cardsRef.current?.offsetHeight || 0;
        main.style.setProperty('--grid-extra-offset', `${base}px`);
        const grid = panel.querySelector('#ipermit-grid-container');
        if (!grid) return;
        // Reading scrollHeight lays the page out with `base` applied.
        const overflow = panel.scrollHeight - panel.clientHeight;
        if (overflow <= 0) return;
        const shrink = Math.min(overflow, Math.max(0, grid.offsetHeight - MIN_GRID_HEIGHT));
        main.style.setProperty('--grid-extra-offset', `${base + shrink}px`);
      });
    };
    fitGrids();
    // A pass ends at the size it started from, so the observer does not fire again for it.
    const observer = new ResizeObserver(fitGrids);
    [main, cardsRef.current, panel].forEach((node) => node && observer.observe(node));
    // A grid mounts after its tab does (it waits on its column options), often without anything
    // here changing size, so watch for it to appear and fit it then.
    let watchedGrid = null;
    const watchGrid = () => {
      const grid = panel.querySelector('#ipermit-grid-container');
      if (!grid || grid === watchedGrid) return;
      watchedGrid = grid;
      observer.observe(grid);
      fitGrids();
    };
    watchGrid();
    const mutations = new MutationObserver(watchGrid);
    mutations.observe(panel, {childList: true, subtree: true});
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      mutations.disconnect();
    };
  }, [canViewAllAgentCalls, openTab]);

  const drawer = (
    <List>
      {sortedTabs.map((tab, index) => (
        <ListItem key={tab.api_name}>
          <ListItemButton
            onClick={() => setOpenTab(index)}
            selected={openTab === index}
            sx={{
              border: '1px solid white',
              '&:hover': {
                backgroundColor: COLORS.backgroundMain,
              },
              '& .MuiTypography-root': {
                fontSize: '1rem',
                fontWeight: 500,
              },
              '&.Mui-selected': {
                borderColor: COLORS.globalBlue,
                backgroundColor: COLORS.globalBlue,
                '& .MuiTypography-root': {
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 600,
                },
                '&:hover': {
                  backgroundColor: COLORS.globalBlue,
                },
              },
              borderRadius: '12px',
            }}
          >
            <ListItemText primary={tab.name} />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );

  /**
   * Where the navbar actually ends. `HEIGHT.NAVBAR` is 5px short of the rendered bar, which is why
   * the drawer below already adds 5; `main` starts there too rather than under the bar's edge.
   */
  const contentTop = `${HEIGHT.NAVBAR({isXS: false, number: true}) + 5}px`;
  const mainHeight = `calc(100vh - ${contentTop})`;

  return (
    <Box sx={{display: 'flex', width: '100%'}}>
      <CssBaseline />
      <Box
        sx={{
          flexShrink: {sm: 0},
          '& .MuiDrawer-paper': {
            top: contentTop,
          },
        }}
        aria-label="AI dashboard sections"
      >
        <Drawer
          variant="permanent"
          sx={{
            display: {xs: 'none', sm: 'block'},
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth(query),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          ml: {xs: 0, sm: `${drawerWidth(query)}px`},
          mt: contentTop,
          width: {xs: '100%', sm: `calc(100% - ${drawerWidth(query)}px)`},
          backgroundColor: '#fff',
          height: mainHeight,
          overflow: 'hidden',
          // A column so the panel gets the height left under the cards, not all of `main`'s:
          // panels that fill their parent (`height: 100%`) would otherwise end a card row below
          // the window. Long tabs (the call feeds) scroll inside the panel, so the cards stay put.
          display: 'flex',
          flexDirection: 'column',
        }}
        ref={mainRef}
      >
        {/* Company-wide counts across every agent. `ai/dashboard-summary` refuses an agent, so
            rendering these without the policy would only produce a failed request. */}
        {canViewAllAgentCalls && (
          <Box ref={cardsRef} sx={{flexShrink: 0}}>
            <AiSummaryCards onNavigate={handleCardNavigate} />
          </Box>
        )}
        {/* Positioned so a grid's loader covers this panel only, not the tabs and cards. */}
        <Box ref={panelRef} sx={{flex: '1 1 0', minHeight: 0, overflowY: 'auto', position: 'relative'}}>
          {sortedTabs[openTab]?.panel}
        </Box>
      </Box>
    </Box>
  );
};

const AiDashboard = () => (
  <AppStoreProvider>
    <AiDashboardInner />
  </AppStoreProvider>
);

export default AiDashboard;
